import { useMemo } from 'react';
import { useReportBuilderStore } from '../../stores/reportBuilderStore';
import { CircleNotch, Star, X, Lock } from '@phosphor-icons/react';

export default function MultiModelPicker() {
  const {
    models, modelsLoading, selectedModelKeys, modelLoading,
    toggleModel, getJoinableModelKeys,
  } = useReportBuilderStore();

  const joinableKeys = getJoinableModelKeys();
  const hasPrimary = selectedModelKeys.length > 0;

  const groupedModels = useMemo(() => {
    const groups: Record<string, typeof models> = {};
    for (const m of models) {
      const mod = m.module || 'other';
      if (!groups[mod]) groups[mod] = [];
      groups[mod].push(m);
    }
    return groups;
  }, [models]);

  const moduleLabels: Record<string, string> = {
    core: 'Core', review_cloud: 'Review Cloud', goals: 'Goals', surveys: 'Surveys', pip: 'PIP',
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700">Data Models</label>
        <p className="text-xs text-gray-400 mt-0.5">
          {hasPrimary ? 'Add more models to include columns from related data.' : 'Select one or more data models. The first becomes the primary.'}
        </p>
      </div>

      {/* Selected model chips */}
      {selectedModelKeys.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedModelKeys.map((key, idx) => {
            const meta = models.find(m => m.key === key);
            const isPrimary = idx === 0;
            return (
              <span
                key={key}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${
                  isPrimary ? 'bg-blue-100 text-blue-700' : 'bg-indigo-100 text-indigo-700'
                }`}
              >
                {isPrimary && <Star className="h-3 w-3" weight="fill" />}
                {meta?.name || key}
                <button onClick={() => toggleModel(key)} className="ml-0.5 hover:opacity-70">
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}

      {modelsLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CircleNotch className="h-4 w-4 animate-spin" /> Loading models...
        </div>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-2">
          {Object.entries(groupedModels).map(([mod, items]) => (
            <div key={mod}>
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                {moduleLabels[mod] || mod}
              </p>
              <div className="space-y-0.5">
                {items.map(m => {
                  const isSelected = selectedModelKeys.includes(m.key);
                  const isJoinable = !hasPrimary || isSelected || joinableKeys.has(m.key);
                  return (
                    <label
                      key={m.key}
                      className={`flex items-center gap-2 rounded px-1 py-0.5 text-sm cursor-pointer ${
                        isJoinable ? 'hover:bg-gray-50' : 'opacity-40 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isJoinable || modelLoading}
                        onChange={() => toggleModel(m.key)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="flex-1 truncate">{m.name}</span>
                      {!isJoinable && <Lock className="h-3 w-3 text-gray-400" />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {modelLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CircleNotch className="h-4 w-4 animate-spin" /> Loading model details...
        </div>
      )}
    </div>
  );
}
